import React from "react";

import moment from "moment";

import store from "~/lib/store";
import { useAiActionItemsUserSettingAndAcceptFlowIsOn } from "~/meetingRecorder/utils";

import { Views } from "../constants";
import { useSyncedPersistentState } from "../hooks/useSyncedPersistentState";
import { ActionItemsContext } from "../newActionItems/ActionItemsContext";
import type { GroupType } from "../types";

export const SYNTHETIC_AI_SUGGESTIONS_GROUP_NAME = "AI Suggestions";
export const AI_SUGGESTIONS_MAX_ITEMS = 5;

// Store key for hiding AI suggestions during auto-accept
export const HIDE_AI_SUGGESTIONS_STORE_KEY = "hideAiSuggestionsDuringAutoAccept";
export const aiSuggestionsStore = store.namespace("aiSuggestions");

/**
 * This allows us to differentiate between our auto-generated "AI Suggestions" group and any user-created group with the
 * name "AI Suggestions". It's simpler to support both than validating the group names when they're created/modified.
 *
 * We can rely on the group ID, because any user-created group must have a db ID and the auto-generated one will default
 * to the name string.
 */
export function isGroupSyntheticAISuggestions(group: GroupType): boolean {
	return group.name === SYNTHETIC_AI_SUGGESTIONS_GROUP_NAME && group.id === SYNTHETIC_AI_SUGGESTIONS_GROUP_NAME;
}

export function wasCreatedWithinLastNDays(createdAt: string | null, n: number): boolean {
	if (createdAt == null) {
		return false;
	}
	const createdAtMoment = moment(createdAt);
	const nDaysAgo = moment().subtract(n, "days").startOf("day");
	return createdAtMoment.isSameOrAfter(nDaysAgo);
}

export function useAISuggestionsState(): {
	shouldShowAISuggestions: boolean;
	hideAISuggestions: () => void;
	resetAISuggestionsHiding: () => void;
} {
	const aiActionItemsUserSettingIsOn = useAiActionItemsUserSettingAndAcceptFlowIsOn();
	const { view, filters } = React.useContext(ActionItemsContext);

	// The applyToCurrentSuggestions logic runs asynchronously in the backend, and it would be strange UX for the AI
	// Suggestions to suddenly disappear from the Action Items page whenever the task completed. Instead, we hide
	// the AI Suggestions component as soon as Confirm is clicked.

	// We do this via localStorage because it's the easiest way to communicate information from the User Settings to
	// the Action Items page, and this is only done for UX purposes. It's important that there's an expiry on this
	// value in case it gets set incorrectly and then AI Suggestions can never be viewed again.

	const [shouldHideAISuggestions, setShouldHideAISuggestions] = useSyncedPersistentState(
		aiSuggestionsStore,
		HIDE_AI_SUGGESTIONS_STORE_KEY,
		false,
		false,
		15,
	);

	const hideAiSuggestionsWithExpiration = React.useCallback(() => {
		setShouldHideAISuggestions(true);
	}, [setShouldHideAISuggestions]);

	const resetAISuggestionsHiding = React.useCallback(() => {
		// Remove the key entirely to clear any expiration data and reset to default
		setShouldHideAISuggestions(false);
	}, [setShouldHideAISuggestions]);

	return {
		shouldShowAISuggestions:
			aiActionItemsUserSettingIsOn && view === Views.USER && filters.showAIDetected && !shouldHideAISuggestions,
		hideAISuggestions: hideAiSuggestionsWithExpiration,
		resetAISuggestionsHiding,
	};
}
